import { useCallback } from "react";
import { useDiagram } from "@/context/diagram-context";
import { useLibrary } from "@/context/library-context";
import useSaveComponent from "@/service-hooks/use-save-component-service";
import * as diagramComponentsLib from "@/lib/diagram-components.lib";
import { componentLibraryManager } from "@/lib/component.lib";

// Custom hooks that safely handle missing contexts
const useSafeDiagram = () => {
  try {
    return useDiagram();
  } catch {
    return null;
  }
};

const useLibrarySafe = () => {
  try {
    return useLibrary();
  } catch {
    return { svgLibrary: [], setComponents: () => {} };
  }
};

export const useSaveComponentToLibrary = () => {
  const diagram = useSafeDiagram();
  const { svgLibrary, setComponents } = useLibrarySafe();

  // Save component mutation with success/error handling
  const saveComponentMutation = useSaveComponent((_, error) => {
    if (error) {
      // TODO: Show error toast/notification
    } else {
      // TODO: Show success toast/notification
    }
  });

  const saveComponent = useCallback(async (
    name: string,
    description: string,
    category: string
  ) => {
    if (!diagram || !diagram.diagramComponents || diagram.diagramComponents.length === 0) {
      const errorMessage = "No diagram components to save";
      throw new Error(errorMessage);
    }

    try {
      let selectedShapes = diagram.diagramComponents;
      
      // If a shape is selected, copy those shapes that are selected for saving as component
      if (diagram.selectedShape) {
        selectedShapes = diagramComponentsLib.copy3DShape(
          diagram.diagramComponents,
          diagram.selectedShape
        );
      }

      // Check if the selectedShapes already has a component with the same name, and disallow saving
      const existingComponent = selectedShapes.find((component) => {
        return component.source === "component" && component.shape === name;
      });

      if (existingComponent) {
        const errorMessage = "Cannot save a diagram as a component containing the same component";
        throw new Error(errorMessage);
      }

      // Create new component using the component library manager
      const newComponent = componentLibraryManager.createComponent(
        name,
        description,
        selectedShapes,
        diagram.canvasSize,
        svgLibrary,
        true // overwrite if exists
      );

      if (newComponent) {
        // Render the component
        componentLibraryManager.renderComponent(
          newComponent.id,
          diagram.canvasSize,
          svgLibrary
        );
        
        // Update components state
        setComponents(componentLibraryManager.getAllComponents());

        // If a shape was selected and we created a component from it, replace the existing shapes with the component
        if (diagram.selectedShape) {
          const selectedShapeObj = diagramComponentsLib.get3DShape(
            diagram.diagramComponents,
            diagram.selectedShape
          );
          const position = selectedShapeObj?.position || "top";
          const parentId = selectedShapeObj?.relativeToId || diagram.diagramComponents[0].id;
          
          const updatedComponents = diagramComponentsLib.remove3DShape(
            diagram.diagramComponents,
            diagram.selectedShape
          );
          
          const result = diagramComponentsLib.addComponentToScene(
            updatedComponents,
            newComponent.id,
            position,
            null,
            parentId
          );
          
          if (result.newComponent) {
            diagram.setDiagramComponents(result.updatedComponents);
            diagram.selectShape(result.newComponent.id);
          } else {
          }
        }

        // Get the component to save and call the API
        const newComponentToSave = componentLibraryManager.getComponent(newComponent.id);
        if (newComponentToSave) {
          saveComponentMutation.mutate({
            component: newComponentToSave,
            category
          });
        }
        
        return newComponent;
      }
      return null;
    } catch (error) {
      throw error;
    }
  }, [diagram, svgLibrary, setComponents, saveComponentMutation]);

  return {
    saveComponent,
    isLoading: saveComponentMutation.isPending,
    error: saveComponentMutation.error,
    isSuccess: saveComponentMutation.isSuccess,
    reset: saveComponentMutation.reset
  };
};